/**
 * Color Preset Picker Component
 * Allows users to select from predefined color schemes that update the entire app
 */

import { Check } from 'lucide-react'

export const ColorPresetPicker = ({
    selectedPreset,
    onSelectPreset,
    className = ''
}) => {
    const presets = [
        {
            id: 'purple',
            name: 'Purple',
            primary: '#6366F1',
            secondary: '#8B5CF6',
            gradient: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)'
        },
        {
            id: 'pink',
            name: 'Pink',
            primary: '#EC4899',
            secondary: '#F472B6',
            gradient: 'linear-gradient(135deg, #EC4899 0%, #F472B6 100%)'
        },
        {
            id: 'dark',
            name: 'Dark',
            primary: '#1F2937',
            secondary: '#374151',
            gradient: 'linear-gradient(135deg, #1F2937 0%, #374151 100%)'
        },
        {
            id: 'blue',
            name: 'Blue',
            primary: '#0EA5E9',
            secondary: '#3B82F6',
            gradient: 'linear-gradient(135deg, #0EA5E9 0%, #3B82F6 100%)'
        },
        {
            id: 'orange',
            name: 'Orange',
            primary: '#F97316',
            secondary: '#FB923C',
            gradient: 'linear-gradient(135deg, #F97316 0%, #FB923C 100%)'
        },
        {
            id: 'green',
            name: 'Green (Default)',
            primary: '#21D19F',
            secondary: '#45B869',
            gradient: 'linear-gradient(135deg, #21D19F 0%, #45B869 100%)'
        }
    ]

    const handleSelectPreset = (preset) => {
        // Update CSS variables
        document.documentElement.style.setProperty('--accent-primary', preset.primary)
        document.documentElement.style.setProperty('--accent-secondary', preset.secondary)

        // Extract RGB values for glow effect
        const hexToRgb = (hex) => {
            const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
            return result ? {
                r: parseInt(result[1], 16),
                g: parseInt(result[2], 16),
                b: parseInt(result[3], 16)
            } : null
        }

        const rgb = hexToRgb(preset.primary)
        if (rgb) {
            document.documentElement.style.setProperty('--accent-primary-rgb', `${rgb.r}, ${rgb.g}, ${rgb.b}`)
            document.documentElement.style.setProperty('--accent-glow', `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.25)`)
        }

        // Callback to parent
        onSelectPreset?.(preset)
    }

    return (
        <div className={className}>
            <label className="label-premium mb-4 block">
                Kies een kleurenschema
            </label>

            <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                {presets.map((preset) => (
                    <button
                        key={preset.id}
                        type="button"
                        onClick={() => handleSelectPreset(preset)}
                        className={`
              relative group
              aspect-square rounded-xl
              border-2 transition-all duration-200
              hover:scale-105 hover:shadow-lg
              ${selectedPreset === preset.id
                                ? 'border-white shadow-lg scale-105'
                                : 'border-white/30 hover:border-white/60'
                            }
            `.trim().replace(/\s+/g, ' ')}
                        style={{ background: preset.gradient }}
                    >
                        {/* Check icon when selected */}
                        {selectedPreset === preset.id && (
                            <div className="absolute inset-0 flex items-center justify-center">
                                <div className="p-1.5 bg-white rounded-full shadow-lg">
                                    <Check size={16} className="text-gray-900" />
                                </div>
                            </div>
                        )}

                        {/* Preset name */}
                        <div className="absolute bottom-0 left-0 right-0 p-2 bg-black/20 backdrop-blur-sm rounded-b-xl">
                            <p className="text-[10px] font-semibold text-white text-center leading-tight">
                                {preset.name}
                            </p>
                        </div>
                    </button>
                ))}
            </div>

            <p className="text-sm text-gray-600 mt-4">
                Het gekozen kleurenschema wordt toegepast op knoppen, links en accenten door de hele app.
            </p>
        </div>
    )
}

/**
 * Hook to get current color preset
 */
export const useColorPreset = () => {
    const getCurrentPreset = () => {
        const primary = getComputedStyle(document.documentElement)
            .getPropertyValue('--accent-primary')
            .trim()

        const presetMap = {
            '#6366F1': 'purple',
            '#EC4899': 'pink',
            '#1F2937': 'dark',
            '#0EA5E9': 'blue',
            '#F97316': 'orange',
            '#21D19F': 'green'
        }

        return presetMap[primary] || 'green'
    }

    return { currentPreset: getCurrentPreset() }
}
